// Giriş ekranı — plan §4.1.1 + design ref #screen-login.
// AuthShell kullanır (sol brand + sağ form).

import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "../stores/auth-store";
import { AuthShell } from "../components/auth/AuthShell";

// Dev mode prefill — apps/frontend/.env.local içindeki VITE_DEV_EMAIL / VITE_DEV_PASSWORD
// vars'larıyla form otomatik dolar. Prod build'de `import.meta.env.DEV === false` → boş.
// .env.local gitignore'da; prod asla prefill görmez.
const DEV_EMAIL = import.meta.env.DEV ? (import.meta.env.VITE_DEV_EMAIL ?? "") : "";
const DEV_PASSWORD = import.meta.env.DEV ? (import.meta.env.VITE_DEV_PASSWORD ?? "") : "";

export function Login(): JSX.Element {
  const login = useAuthStore((s) => s.login);
  const status = useAuthStore((s) => s.status);
  const error = useAuthStore((s) => s.error);

  const [email, setEmail] = useState(DEV_EMAIL);
  const [password, setPassword] = useState(DEV_PASSWORD);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      await login(email, password);
    } catch {
      // Auth store error'ı yakaladı, UI'da gösterilecek
    }
  }

  const isLoading = status === "loading";

  return (
    <AuthShell>
      <form onSubmit={(e) => void onSubmit(e)} aria-labelledby="login-heading">
        <h2
          id="login-heading"
          className="text-3xl mb-2"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
        >
          Giriş yap
        </h2>
        <p className="mb-8" style={{ color: "var(--color-text-2)" }}>
          Danışmanlık hesabınıza giriş yapın.
        </p>

        <label className="block mb-5">
          <span className="block mb-1.5 text-sm font-medium" style={{ color: "var(--color-text)" }}>
            E-posta
          </span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            className="w-full px-3 py-2.5 rounded-lg border focus:outline-none"
            style={{
              background: "var(--color-surface)",
              borderColor: "var(--color-border)",
              color: "var(--color-text)",
              fontSize: "0.95rem",
            }}
            placeholder="siz@sirketiniz.com"
          />
        </label>

        <label className="block mb-2">
          <span className="block mb-1.5 text-sm font-medium" style={{ color: "var(--color-text)" }}>
            Şifre
          </span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            className="w-full px-3 py-2.5 rounded-lg border focus:outline-none"
            style={{
              background: "var(--color-surface)",
              borderColor: "var(--color-border)",
              color: "var(--color-text)",
              fontSize: "0.95rem",
            }}
            placeholder="••••••••••••"
          />
        </label>

        <div className="mb-6 text-right">
          <Link
            to="/forgot"
            style={{ color: "var(--color-primary)", fontSize: "0.875rem" }}
            className="hover:underline"
          >
            Şifremi unuttum
          </Link>
        </div>

        {error ? (
          <div
            role="alert"
            data-alert-variant="error"
            className="mb-4 px-3 py-2 rounded-lg text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
              border: "1px solid color-mix(in srgb, var(--color-error) 35%, white)",
            }}
          >
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={isLoading || !email || !password}
          className="w-full px-4 py-3 rounded-lg font-semibold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: "var(--color-primary)",
            color: "var(--color-surface)",
            fontSize: "0.95rem",
          }}
        >
          {isLoading ? "Giriş yapılıyor…" : "Giriş Yap"}
        </button>

        {import.meta.env.DEV && (DEV_EMAIL || DEV_PASSWORD) ? (
          <p
            className="mt-4 text-xs text-center"
            style={{ color: "var(--color-warning)", fontFamily: "var(--font-mono)" }}
          >
            ⚙ Dev: VITE_DEV_* ile otomatik dolduruldu
          </p>
        ) : null}

        <p
          className="mt-6 text-xs text-center"
          style={{ color: "var(--color-text-3)", fontFamily: "var(--font-mono)" }}
        >
          &copy; {new Date().getFullYear()} Mahir AI
        </p>
      </form>
    </AuthShell>
  );
}
